import { mainIconSize } from '@/constants';
import pageStyles from '@/styles/pages/index.module.css';
import type { UserPublic } from '@/types';
import { Breadcrumbs } from '@nautilusbio/nb-design-system/Breadcrumbs';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Link } from '@tanstack/react-router';
import { PencilLine, Save, X } from 'lucide-react';
import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type UserActionRowProps = {
  user: UserPublic;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
};

export const UserActionRow: FC<UserActionRowProps> = ({ user, isEditing, onEdit, onCancel, onSave }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tUsers } = useTranslation('users');

  return (
    <Stack alignItems="center" className={pageStyles.titleRow} justifyContent="space-between" orientation="horizontal">
      <Breadcrumbs
        links={[
          { id: 'users', children: tUsers('title'), to: '/users' },
          { id: 'user', children: user.full_name },
        ]}
        renderLink={(link, _index, defaultLinkProps) => {
          if (!link?.to) return link?.children;
          return (
            <Link className={defaultLinkProps.className} to={link.to as '/users'}>
              {link.children}
            </Link>
          );
        }}
        separator="arrow"
        size="md"
      />

      <Stack alignItems="center" gap={2} orientation="horizontal">
        {isEditing ? (
          <>
            <Button data-testid="user-action-cancel" onPress={onCancel} size="md" visual="unstyled">
              <X size={mainIconSize} />
              {tGeneral('cancel')}
            </Button>
            <Button data-testid="user-action-save" onPress={onSave} size="md" visual="unstyled">
              <Save size={mainIconSize} />
              {tGeneral('save')}
            </Button>
          </>
        ) : (
          <Tooltip
            content={tUsers('edit_coming_soon')}
            placement="bottom"
            trigger={
              <Button data-testid="user-action-edit" isDisabled onPress={onEdit} size="md" visual="unstyled">
                <PencilLine size={mainIconSize} />
                {tGeneral('edit')}
              </Button>
            }
          />
        )}
      </Stack>
    </Stack>
  );
};
